import React, { useState, useEffect, useRef } from 'react';
import { Outlet, useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Badge } from './ui/badge';
import logger from '../lib/logger';
import {
  ArrowLeft, Clock, DollarSign, Loader2, FileText, FileEdit, GitCompareArrows, Lock, Archive, ArchiveRestore,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { usePaletteCommands, useCommandPalette } from '../context/CommandPaletteContext';
import { resolveDisplayRounding } from '../lib/rounding';
import { getCurrencySymbol } from '../lib/currency';
import RoundedMetric from './RoundedMetric';
import EditConditionsDialog from './EditConditionsDialog';
import CSLeadHandoverNudge from './CSLeadHandoverNudge';
import EstimateVersionChip from './estimator/EstimateVersionChip';
import { EstimateLockControl } from './estimator/EstimateLockControl';
import WorkspaceSubNav, { computeEstimateProgress } from './WorkspaceSubNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Map URL pathname → subnav active-tab id. Centralised here so the workspace
// layout and the Estimator's wizard both highlight the same tab when the
// user navigates between the two surfaces.
function tabIdFromPath(pathname) {
  if (!pathname) return null;
  if (pathname.endsWith('/overview')) return 'overview';
  if (pathname.endsWith('/compare')) return 'compare';
  if (pathname.endsWith('/planner')) return 'planner';
  if (pathname.endsWith('/product-pricing')) return 'product-pricing';
  if (pathname.endsWith('/quote')) return 'quote';
  if (pathname.endsWith('/design-pack')) return 'design-pack';
  if (pathname.endsWith('/activity')) return 'activity';
  if (pathname.endsWith('/comments')) return 'comments';
  if (pathname.endsWith('/documents')) return 'documents';
  return null;
}

export default function EstimateWorkspaceLayout() {
  const { estimateId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  // Quick-edit Project Conditions sheet (only relevant for estimates with
  // `has_conditions=true`; legacy estimates remain unaffected).
  const [conditionsOpen, setConditionsOpen] = useState(false);
  const [conditionsHighlightBreakId, setConditionsHighlightBreakId] = useState(null);
  // Listen for cross-page drill-down requests (e.g. the Planner's "N
  // components overlap" chip dispatches `open-project-conditions` with
  // a breakId so the flyout opens already pulsing the colliding break.
  useEffect(() => {
    const handler = (e) => {
      const breakId = e?.detail?.breakId || null;
      setConditionsHighlightBreakId(breakId);
      setConditionsOpen(true);
    };
    window.addEventListener('open-project-conditions', handler);
    return () => window.removeEventListener('open-project-conditions', handler);
  }, []);
  // Track which estimateIds we've already pinged for visit-tracking this
  // mount so reloadEstimate (called from initial load + Outlet children)
  // only fires the /visit POST once per workspace open. The /touch ping
  // is left as-is since it's already throttled server-side.
  const visitedRef = useRef(new Set());

  const reloadEstimate = React.useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/estimates/${estimateId}`, { withCredentials: true });
      setEstimate(res.data);
      // Fire-and-forget view ping so the ⌘K palette's "Recent
      // estimates" group can sort by the user's own activity.
      axios
        .post(`${API_URL}/api/estimates/${estimateId}/touch`, {}, { withCredentials: true })
        .catch(() => { /* silent — view tracking is non-critical */ });
      // Mark this estimate as "visited" for the pinned-rail activity
      // badge. Deduped via visitedRef so a single workspace open only
      // emits one /visit POST regardless of how many children call
      // reloadEstimate during that mount.
      if (!visitedRef.current.has(estimateId)) {
        visitedRef.current.add(estimateId);
        axios
          .post(`${API_URL}/api/users/me/pinned-estimates/${estimateId}/visit`, {}, { withCredentials: true })
          .catch(() => { /* silent — last-visit ping is non-critical */ });
      }
    } catch (err) {
      logger.error('Failed to load estimate:', err);
    }
  }, [estimateId]);

  useEffect(() => {
    (async () => {
      await reloadEstimate();
      setLoading(false);
    })();
  }, [reloadEstimate]);

  // ── Contextual ⌘K commands while viewing an estimate workspace ──
  // Surfaces "Compare this with…" — pushes a sub-step that loads
  // candidate estimates, then navigates to /compare?ids={current},{picked}
  // so the comparison auto-runs on land.
  const { pushStep, setOpen } = useCommandPalette();
  usePaletteCommands(
    'workspace-current-actions',
    () => {
      const items = [];
      if (estimate?.id) {
        items.push({
          id: 'ws-compare-current-with',
          value: `Compare ${estimate.estimate_name || 'this estimate'} with another`,
          icon: GitCompareArrows,
          label: `Compare "${estimate.estimate_name || 'this estimate'}" with…`,
          closeOnSelect: false,
          onSelect: () => {
            pushStep({
              id: 'pick-estimate-to-compare',
              label: `Compare "${estimate.estimate_name || 'this'}" with`,
              placeholder: 'Pick an estimate to compare against…',
              loadItems: async () => {
                const r = await axios.get(`${API_URL}/api/estimates`, { params: { limit: 25 } });
                const list = Array.isArray(r.data) ? r.data : (r.data?.estimates || []);
                return list
                  .filter((e) => e.id !== estimate.id)
                  .slice(0, 20)
                  .map((e) => ({
                    id: `cmp-${e.id}`,
                    value: `${e.estimate_name || ''} ${e.organization_name || ''} ${e.implementation_type || ''} ${e.id}`,
                    icon: FileText,
                    label: e.estimate_name || e.organization_name || 'Untitled estimate',
                    sublabel: [e.organization_name, e.implementation_type].filter(Boolean).join(' · '),
                    onSelect: () => {
                      setOpen(false);
                      setTimeout(() => navigate(`/compare?ids=${estimate.id},${e.id}&autorun=1`), 60);
                    },
                  }));
              },
            });
          },
        });
        items.push({
          id: 'ws-edit-current',
          value: `Edit ${estimate.estimate_name || 'estimate'} in estimator`,
          icon: FileEdit,
          label: 'Open in Estimator (edit)',
          onSelect: () => navigate(`/estimator?load=${estimate.id}`),
        });
      }
      return items.length > 0
        ? { heading: estimate?.estimate_name ? `Current — ${estimate.estimate_name}` : 'Current estimate', priority: 1, items }
        : null;
    },
    { enabled: !!estimate?.id, deps: [estimate?.id, estimate?.estimate_name, navigate] }
  );

  // Determine active tab from URL
  const activeTab = tabIdFromPath(location.pathname) || 'overview';

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-[#4ABFCF] mx-auto" />
          <p className="mt-3 text-sm text-slate-500">Loading workspace...</p>
        </div>
      </div>
    );
  }

  if (!estimate) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <p className="text-slate-500">Estimate not found.</p>
          <button onClick={() => navigate('/history')} className="mt-3 text-[#4ABFCF] hover:underline text-sm">
            Back to History
          </button>
        </div>
      </div>
    );
  }

  const restoreArchived = async () => {
    try {
      await axios.post(`${API_URL}/api/estimates/${estimateId}/unarchive`, {}, { withCredentials: true });
      toast.success('Estimate restored to active workspaces');
      reloadEstimate();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to restore'); }
  };

  return (
    <div data-testid="estimate-workspace">
      {estimate.archived_at && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 sm:px-6 lg:px-8 py-2 flex flex-wrap items-center gap-3 text-xs text-amber-800" data-testid="workspace-archived-banner">
          <Archive className="w-4 h-4" />
          <span><b>This estimate is archived</b> (since {String(estimate.archived_at).slice(0, 10)}) — it is hidden from Workspaces, Insights and reports. You can still view it read-only here.</span>
          <button type="button" onClick={restoreArchived} className="ml-auto inline-flex items-center gap-1 rounded-md bg-[#0F2040] px-2.5 py-1 text-[11px] font-medium text-white hover:bg-[#1a3260]" data-testid="workspace-restore-btn">
            <ArchiveRestore className="w-3.5 h-3.5" /> Restore estimate
          </button>
        </div>
      )}
      {/* Workspace Header */}
      <div className="bg-white border-b border-slate-200" data-testid="workspace-header">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => navigate('/history')}
                className="shrink-0 p-1.5 rounded-md hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600"
                data-testid="workspace-back-btn"
                title="Back to History"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div className="min-w-0">
                <h1 className="text-lg font-semibold text-[#0F2040] truncate font-['Outfit']" data-testid="workspace-estimate-name">
                  {estimate.estimate_name}
                  {estimate.version_number != null && (
                    <span className="ml-2 inline-block align-middle">
                      <EstimateVersionChip
                        estimateId={estimate.id}
                        versionNumber={estimate.version_number}
                        onRestored={() => window.location.reload()}
                      />
                    </span>
                  )}
                  {estimate.id && (
                    <span className="ml-2 inline-block align-middle">
                      <EstimateLockControl estimateId={estimate.id} compact refreshKey={estimate.workflow_status || estimate.approval_status || ''} />
                    </span>
                  )}
                </h1>
                <div className="flex items-center gap-2 mt-0.5">
                  <Badge variant="outline" className="text-[10px] py-0">
                    {estimate.implementation_type?.replace('_', ' ')}
                  </Badge>
                  <Badge variant="outline" className="text-[10px] py-0">
                    {estimate.complexity}
                  </Badge>
                  {estimate.has_conditions && (
                    <button
                      onClick={() => setConditionsOpen(true)}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#4ABFCF]/10 hover:bg-[#4ABFCF]/20 border border-[#4ABFCF]/30 text-[10px] font-semibold text-[#0F2040] transition-colors"
                      data-testid="workspace-edit-conditions-btn"
                      title="Edit Project Conditions"
                    >
                      <Lock className="w-2.5 h-2.5" />
                      Project Conditions
                    </button>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-5 shrink-0">
              {(() => {
                const rounding = resolveDisplayRounding(estimate, user);
                const effort = estimate.results?.total_effort || 0;
                const cost = estimate.results?.total_cost || 0;
                const currencyCode = (
                  estimate.currency_code
                  || user?.preferences?.default_currency_code
                  || 'USD'
                );
                return (
                  <>
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-slate-400 text-[10px] justify-end">
                        <Clock className="w-3 h-3" />
                        Effort
                      </div>
                      <p className="text-sm font-bold text-[#0F2040] font-['Outfit']">
                        <RoundedMetric
                          kind="hours"
                          value={effort}
                          rounding={rounding}
                          estimate={estimate}
                          user={user}
                          testId="workspace-effort"
                        />
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-1 text-slate-400 text-[10px] justify-end">
                        <DollarSign className="w-3 h-3" />
                        Cost
                      </div>
                      <p className="text-sm font-bold text-[#4ABFCF] font-['Outfit']">
                        <RoundedMetric
                          kind="cost"
                          value={cost}
                          rounding={rounding}
                          estimate={estimate}
                          user={user}
                          currencyCode={currencyCode}
                          symbol={getCurrencySymbol(currencyCode)}
                          testId="workspace-cost"
                        />
                      </p>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Sub-navigation tabs — single source of truth in WorkspaceSubNav
            so the Estimator wizard and the workspace layout render an
            identical strip (same items, same active-state styling). */}
        <WorkspaceSubNav
          estimateId={estimateId}
          activeTab={activeTab}
          hasConditions={!!estimate?.has_conditions}
          onProjectConditionsClick={() => setConditionsOpen(true)}
          customerName={estimate?.organization_name}
          projectName={estimate?.implementation_name || estimate?.estimate_name}
          progress={computeEstimateProgress(estimate)}
        />
      </div>

      {/* Sales→CS handover nudge — Won/Approved deal with no CS Lead yet. */}
      <CSLeadHandoverNudge estimate={estimate} onAssignClick={() => setConditionsOpen(true)} />

      {/* Tab Content */}
      <Outlet context={{ estimateId, estimate, reloadEstimate }} />

      {/* Project Conditions dialog — also opens for legacy estimates so
          they can migrate to the modern Conditions Flow. */}
      <EditConditionsDialog
        open={conditionsOpen}
        onOpenChange={(o) => { setConditionsOpen(o); if (!o) setConditionsHighlightBreakId(null); }}
        estimateId={estimateId}
        initialConditions={estimate?.project_conditions}
        highlightBreakId={conditionsOpen ? conditionsHighlightBreakId : null}
        isMigration={!estimate?.has_conditions}
        onSaved={reloadEstimate}
      />
    </div>
  );
}
